import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stravaApi } from '@/api/strava';
import { workoutApi } from '@/api/workouts';
import { ApiError } from '@/api/client';
import { workoutsKey } from '@/hooks/useWorkouts';
import { workoutStatsKey } from '@/hooks/useWorkoutStats';
import type { StravaStatus } from '@/types/User';

export const stravaStatusKey = ['strava', 'status'] as const;

// /sync only emits ActivityImported events to Kafka; cardio ingests them asynchronously, so
// imported workouts trickle into the list after the request returns. Instead of a single
// immediate refetch (which shows nothing), poll the list until ingestion settles.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 90_000;
const PLATEAU_POLLS = 2; // consecutive unchanged polls (after growth) → ingestion settled
const NO_GROWTH_GIVE_UP_MS = 18_000; // nothing arrived → stop waiting

export function useStrava(enabled: boolean) {
  const qc = useQueryClient();

  const [importing, setImporting] = useState(false);
  const [emitted, setEmitted] = useState<number | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);

  useEffect(
    () => () => {
      if (pollRef.current) pollRef.current.cancelled = true;
    },
    [],
  );

  const query = useQuery<StravaStatus | null, Error>({
    queryKey: stravaStatusKey,
    queryFn: async () => {
      try {
        return await stravaApi.status();
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return null;
        }
        throw err;
      }
    },
    enabled,
  });

  const disconnectMut = useMutation({
    mutationFn: () => stravaApi.disconnect(),
    onSuccess: () => qc.invalidateQueries({ queryKey: stravaStatusKey }),
  });

  const syncMut = useMutation({
    mutationFn: () => stravaApi.sync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: stravaStatusKey }),
  });

  // Server-side total: the workouts list is paginated, so cached length ≠ count.
  const countWorkouts = useCallback(
    () =>
      workoutApi
        .list(0, 1)
        .then((p) => p.totalElements)
        .catch(() => 0),
    [],
  );

  const refreshLists = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: workoutsKey }),
      qc.invalidateQueries({ queryKey: workoutStatsKey }),
    ]);
  }, [qc]);

  const pollUntilSettled = useCallback(
    async (baseline: number) => {
      if (pollRef.current) pollRef.current.cancelled = true;
      const token = { cancelled: false };
      pollRef.current = token;

      setImporting(true);
      const start = Date.now();
      let lastCount = baseline;
      let stable = 0;
      let grew = false;

      while (!token.cancelled && Date.now() - start < POLL_MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (token.cancelled) return;

        await refreshLists();
        const current = await countWorkouts();
        if (token.cancelled) return;
        setImportedCount(Math.max(0, current - baseline));

        if (current > lastCount) {
          grew = true;
          stable = 0;
          lastCount = current;
        } else {
          stable += 1;
        }

        if (grew && stable >= PLATEAU_POLLS) break;
        if (!grew && Date.now() - start >= NO_GROWTH_GIVE_UP_MS) break;
      }

      if (!token.cancelled) {
        setImporting(false);
        qc.invalidateQueries({ queryKey: stravaStatusKey });
      }
    },
    [countWorkouts, refreshLists, qc],
  );

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: stravaStatusKey });
  }, [qc]);

  const connect = useCallback(() => {
    window.location.href = stravaApi.authorizeUrl();
  }, []);

  const disconnect = useCallback(async () => {
    if (pollRef.current) pollRef.current.cancelled = true;
    setImporting(false);
    setEmitted(null);
    setImportedCount(null);
    await disconnectMut.mutateAsync();
  }, [disconnectMut]);

  const sync = useCallback(async () => {
    const baseline = await countWorkouts();
    setEmitted(null);
    setImportedCount(null);

    const result = await syncMut.mutateAsync();
    const emittedCount = result.emitted ?? 0;
    setEmitted(emittedCount);
    if (emittedCount > 0) setImporting(true);

    await refreshLists();
    if (emittedCount > 0) {
      void pollUntilSettled(baseline);
    }
    return result;
  }, [countWorkouts, syncMut, refreshLists, pollUntilSettled]);

  return {
    status: query.data ?? null,
    loading: query.isPending && enabled,
    syncing: syncMut.isPending,
    importing,
    emitted,
    importedCount,
    syncError: syncMut.error,
    refresh,
    connect,
    disconnect,
    sync,
  };
}
